import { useEffect, useRef } from "react";
import { useWebSocketContext } from "./useWebSocketContext";
import type { WebSocketEventHandler, WebSocketEventMap } from "./types";

type UseWebSocketEventOptions = {
	readonly enabled?: boolean;
};

/**
 * Inscreve-se em um evento conhecido do servidor. O tipo do payload do handler
 * é inferido automaticamente a partir de `WebSocketEventMap`.
 */
export function useWebSocketEvent<K extends keyof WebSocketEventMap>(
	eventName: K,
	handler: WebSocketEventHandler<WebSocketEventMap[K]>,
	options?: UseWebSocketEventOptions,
): void;

export function useWebSocketEvent(
	eventName: string,
	handler: WebSocketEventHandler<unknown>,
	options?: UseWebSocketEventOptions,
): void {
	const { subscribe } = useWebSocketContext();
	const handlerRef = useRef(handler);
	const enabled = options?.enabled ?? true;

	useEffect(() => {
		handlerRef.current = handler;
	}, [handler]);

	useEffect(() => {
		if (!enabled) return undefined;

		return subscribe(eventName, (payload, ...args) => {
			handlerRef.current(payload, ...args);
		});
	}, [enabled, eventName, subscribe]);
}
